import { GetIntersectionObserver } from "@pdfking/capability"
import { CreateMixin } from "@pdfking/reactivity/preact"

import { RootMixin } from "./RootMixin"

const Observer = GetIntersectionObserver()

// Exposes `visible`: tracks whether the component's root element currently
// intersects its scroll container ([data-ref=reader]), toggling both ways as
// it scrolls in and out — so an element that has scrolled away drops back to
// deferring its heavy per-element work instead of paying for it forever.
// Falls back to visible when IntersectionObserver is unavailable. Use it to
// defer heavy per-element work.
//
// `seen` is the same signal latched: false until the element first intersects,
// true forever after. Gate on it instead of `visible` when the work is worth
// keeping once done — scrolling back would only rebuild it — and on `visible`
// when it must be released as soon as the element leaves.
export const ObservedMixin = CreateMixin({
  data: {
    observer: undefined as IntersectionObserver | undefined,
    seen: false,
    visible: false,
  },
  mixins: [RootMixin] as const,
  props: {
    target: undefined as string | undefined,
    margin: "200px" as string,
  },
  mounted(el: HTMLElement) {
    if (!Observer || !el) {
      this.visible = true
      this.seen = true
      return
    }
    this.observer = new Observer(
      // Only `el` is observed, so each callback carries a single entry for it —
      // `visible` just mirrors whether it currently intersects.
      entries => {
        this.visible = entries.some(e => e.isIntersecting)
        if (this.visible) this.seen = true
      },
      {
        root: this.target ? el.closest(this.target) : el.parentElement,
        rootMargin: this.margin,
        threshold: 0,
      }
    )
    this.observer.observe(el)
  },
  unmounted() {
    this.observer?.disconnect()
  },
})
